// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { TimePeriod } from "types";
import { TrendMonitor as ITrendMonitor } from "dbtypes";
import {
  IAction,
  ActionType,
  raiseAction,
  IActionTypeSetPeriod,
  ActionGraphCommand,
} from "actions";
import { isSet } from "utils";
import { IState } from "states/states";

import PeriodSelect from "view/comp/PeriodSelect";
import TrendMonitorGraph from "view/comp/TrendMonitorGraph";
import TrendHeading from "view/comp/TrendHeading";
import TrendMonitorSelect from "view/comp/TrendMonitorSelect";
import { getSiteSelection } from "guiutils";

interface ITrendMonitorExProps {
  isDashboard?: boolean;
}
interface ITrendMonitorProps {
  selectedMonitor: number | undefined;
  trendMonitors: ITrendMonitor[];
}

const mapStateToProps = (state: IState) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  const props: ITrendMonitorProps = {
    selectedMonitor: getSiteSelection(state)?.trendMonitor,
    trendMonitors: site?.cloudSettings?.trendMonitor ?? [],
  };
  return props;
};

interface ITrendMonitorDispatch {
  onTrendMonitorChanged: (
    trendMonitors: ITrendMonitor[],
    name: string,
    value: string | number,
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendMonitorDispatch = {
    onTrendMonitorChanged: (
      trendMonitors: ITrendMonitor[],
      name: string,
      value: string | number,
    ) => {
      dispatch(
        raiseAction(ActionType.ACT_EDIT_VALUE, {
          name,
          value,
        }),
      );
      // also update timePeriod
      const selectedTrendMonitor = trendMonitors[value as number];
      const payload: IActionTypeSetPeriod = {
        identifier: "trendPeriod",
        command: ActionGraphCommand.SET_PERIOD,
        period: selectedTrendMonitor.period as unknown as TimePeriod,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
      // load data
      dispatch(raiseAction(ActionType.ACT_UPDATE_TREND));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  graphTitle: {
    id: "TrendGraphTitle",
    defaultMessage: "Trend Monitor",
  },
});

class TrendMonitorComponent extends React.Component<
  ITrendMonitorProps & ITrendMonitorExProps & ITrendMonitorDispatch & WrappedComponentProps
> {
  public render() {
    const trendMonitor = this.props.trendMonitors[this.props.selectedMonitor!] as
      | ITrendMonitor
      | undefined;
    return (
      <div id="trendmonitor" className="flex-self flex-content graph-shrink">
        {this.props.isDashboard !== true && (
          <div className="headerbuttons mb-2">
            <div className="left">
              <TrendMonitorSelect />
            </div>

            <PeriodSelect
              identifier="trendPeriod"
              periodOptions={
                trendMonitor?.period ? [trendMonitor.period as unknown as TimePeriod] : undefined
              }
              updateAction={ActionType.ACT_UPDATE_TREND}
            />
          </div>
        )}
        {this.props.isDashboard !== true && (
          <TrendHeading
            identifier="trendPeriod"
            includeDate={true}
            title={this.props.intl.formatMessage(messages.graphTitle)}
          />
        )}
        {isSet(trendMonitor) && <TrendMonitorGraph />}
      </div>
    );
  }
}

const TrendMonitor = connect(mapStateToProps, mapDispatchToProps)(TrendMonitorComponent);

export default injectIntl(TrendMonitor);
